// src/components/workloads/creation/WorkloadNamingStep.tsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  FileText,
  Folder,
  Users,
  UserPlus,
  ClipboardList,
  Search,
  Sparkles,
} from 'lucide-react';
import { useWorkloadStore } from '../../../store/workloadStore';
import { useAuth } from '../../../contexts/AuthContext';
import { projectService } from '../../../services/project.service';
import { surveyService } from '../../../services/survey.service';
import { getUsers } from '../../../services/user.service';
import { Project } from '../../../types/project.types';
import { Survey } from '../../../types/survey.types';
import { User } from '../../../types/user.types';

const WorkloadNamingStep: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const {
    creation,
    setCreationName,
    setCreationProject,
    setCreationSurvey,
    setCreationAssignedTo,
    setCreationAssignedToNames,
  } = useWorkloadStore();

  const [projects, setProjects] = useState<Project[]>([]);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      if (!currentUser) return;

      try {
        const projectList = await projectService.getUserProjects(currentUser.uid);
        setProjects(projectList);

        // Auto-select if only one project
        if (projectList.length === 1 && !creation.projectId) {
          setCreationProject(projectList[0].id);
        }
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProjects();
  }, [currentUser, creation.projectId, setCreationProject]);

  // Load surveys when project changes
  useEffect(() => {
    const loadSurveys = async () => {
      try {
        // First try to get surveys for the specific project
        let surveyList: Survey[] = [];

        if (creation.projectId) {
          surveyList = await surveyService.getActiveSurveys(creation.projectId);
        }

        // If no project-specific surveys found, get all available surveys
        if (surveyList.length === 0) {
          surveyList = await surveyService.getAllActiveSurveys();
        }

        setSurveys(surveyList);
      } catch (error) {
        console.error('Error loading surveys:', error);
      }
    };

    loadSurveys();
  }, [creation.projectId]);

  // Load available users for assignment
  useEffect(() => {
    const loadUsers = async () => {
      if (!currentUser) return;

      setIsLoadingUsers(true);
      try {
        const users = await getUsers();
        // Filter to field users and client users who can be assigned workloads
        const assignableUsers = users.filter(
          (u) => u.role === 'field' || u.role === 'client_user'
        );
        setAvailableUsers(assignableUsers);
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    loadUsers();
  }, [currentUser]);

  // Toggle user selection
  const handleUserToggle = (userId: string) => {
    const currentAssigned = creation.assignedTo || [];
    const user = availableUsers.find((u) => u.uid === userId);

    if (currentAssigned.includes(userId)) {
      // Remove user
      const newAssigned = currentAssigned.filter((id) => id !== userId);
      setCreationAssignedTo(newAssigned);
      // Update names
      const newNames = newAssigned.map((id) => {
        const u = availableUsers.find((user) => user.uid === id);
        return u?.name || '';
      });
      setCreationAssignedToNames(newNames);
    } else {
      // Add user
      const newAssigned = [...currentAssigned, userId];
      setCreationAssignedTo(newAssigned);
      // Update names
      const newNames = newAssigned.map((id) => {
        const u = availableUsers.find((user) => user.uid === id);
        return u?.name || '';
      });
      setCreationAssignedToNames(newNames);
    }
  };

  // Filter users by search
  const filteredUsers = availableUsers.filter((user) => {
    if (!userSearchTerm) return true;
    const search = userSearchTerm.toLowerCase();
    return (
      user.name.toLowerCase().includes(search) ||
      user.email.toLowerCase().includes(search)
    );
  });

  // Check if selected project is a lead project
  const selectedProject = useMemo(() => {
    return projects.find(p => p.id === creation.projectId);
  }, [projects, creation.projectId]);

  const isLeadProject = selectedProject?.projectType === 'lead_program';

  // Generate auto-name suggestion
  const generateAutoName = () => {
    const project = projects.find((p) => p.id === creation.projectId);
    const date = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const type =
      creation.type === 'saturation'
        ? 'Saturation'
        : creation.type === 'csv_upload'
        ? 'CSV'
        : 'Census';

    return `${project?.name || 'Workload'} - ${type} - ${date}`;
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Name & Configure Your Workload
        </h2>
        <p className="text-gray-600">
          Set a name, assign to a project, and optionally link a survey.
        </p>
      </div>

      <div className="space-y-6">
        {/* Workload Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <FileText className="h-4 w-4 inline mr-1" />
            Workload Name
            <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={creation.name}
              onChange={(e) => setCreationName(e.target.value)}
              placeholder="Enter a descriptive name..."
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
            />
            <button
              onClick={() => setCreationName(generateAutoName())}
              className="px-3 py-2 text-sm text-indigo-600 border border-indigo-300 rounded-md hover:bg-indigo-50"
            >
              Auto
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            This name will be used to identify the workload in lists and reports.
          </p>
        </div>

        {/* Project Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Folder className="h-4 w-4 inline mr-1" />
            Project
            <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={creation.projectId}
            onChange={(e) => setCreationProject(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">-- Select a project --</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          {projects.length === 0 && !isLoading && (
            <p className="text-xs text-amber-600 mt-1">
              No projects available. Create a project first.
            </p>
          )}
        </div>

        {/* Survey Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <ClipboardList className="h-4 w-4 inline mr-1" />
            Survey / Completion Action
          </label>
          <select
            value={creation.surveyId || ''}
            onChange={(e) => {
              const selectedSurvey = surveys.find(s => s.id === e.target.value);
              setCreationSurvey(
                e.target.value || null,
                selectedSurvey?.name || null
              );
            }}
            disabled={!creation.projectId}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100"
          >
            {isLeadProject ? (
              <option value="">Lead Generation Mode (No Survey)</option>
            ) : (
              <option value="">-- No survey --</option>
            )}
            {surveys.map((survey) => (
              <option key={survey.id} value={survey.id}>
                {survey.name} (v{survey.version}) {survey.status === 'active' ? '✓' : survey.status === 'draft' ? '[Draft]' : ''}
              </option>
            ))}
          </select>

          {/* Lead generation mode hint */}
          {isLeadProject && !creation.surveyId && creation.projectId && (
            <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-start">
                <Sparkles className="h-4 w-4 text-emerald-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">Lead Generation Mode</p>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    When "Completed" is selected, the lead capture form will open automatically to collect parent/child information.
                  </p>
                </div>
              </div>
            </div>
          )}

          {surveys.length === 0 && creation.projectId && !isLeadProject && (
            <p className="text-xs text-amber-600 mt-1">
              No surveys found for this project. Create a survey first in Survey Management.
            </p>
          )}
          {!creation.projectId && (
            <p className="text-xs text-gray-500 mt-1">
              Select a project first to see available options.
            </p>
          )}
          {surveys.length > 0 && creation.surveyId && (
            <p className="text-xs text-gray-500 mt-1">
              This survey will open when an address is marked as "Completed". Surveys marked with ✓ are published.
            </p>
          )}
        </div>

        {/* Assignment Section */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="font-medium text-gray-900 mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-gray-400" />
            Assignment (Optional)
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Select users to assign this workload to. You can also assign users later from the Manage Workloads page.
          </p>

          {/* User search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* User list */}
          {isLoadingUsers ? (
            <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center text-gray-500">
                <UserPlus className="h-5 w-5 mr-2" />
                <span className="text-sm">
                  {userSearchTerm ? 'No users match your search.' : 'No field users available for assignment.'}
                </span>
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
              {filteredUsers.map((user) => {
                const isSelected = creation.assignedTo?.includes(user.uid) || false;
                return (
                  <label
                    key={user.uid}
                    className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleUserToggle(user.uid)}
                      className="h-4 w-4 text-indigo-600 rounded border-gray-300 mr-3"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                      {user.role === 'field' ? 'Field' : 'Client User'}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {creation.assignedTo && creation.assignedTo.length > 0 && (
            <p className="text-sm text-indigo-600 mt-2">
              {creation.assignedTo.length} user{creation.assignedTo.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>

        {/* Summary */}
        <div className="bg-indigo-50 rounded-lg p-4">
          <h4 className="font-medium text-indigo-900 mb-2">Summary</h4>
          <ul className="text-sm text-indigo-700 space-y-1">
            <li>
              <strong>Type:</strong>{' '}
              {creation.type === 'saturation'
                ? 'Saturation Point'
                : creation.type === 'csv_upload'
                ? 'CSV Upload'
                : 'Census Block'}
            </li>
            <li>
              <strong>Addresses:</strong>{' '}
              {creation.addresses.filter((a) => a.selected).length} selected
            </li>
            {creation.listSplitting.enabled && (
              <li>
                <strong>Split into:</strong>{' '}
                {Math.ceil(
                  creation.addresses.filter((a) => a.selected).length /
                    creation.listSplitting.maxSize
                )}{' '}
                workloads (max {creation.listSplitting.maxSize} addresses each)
              </li>
            )}
            <li>
              <strong>On Completion:</strong>{' '}
              {creation.surveyId
                ? surveys.find((s) => s.id === creation.surveyId)?.name || 'Survey'
                : isLeadProject
                  ? 'Lead Generation Mode'
                  : 'No action'
              }
            </li>
            {creation.assignedTo && creation.assignedTo.length > 0 && (
              <li>
                <strong>Assigned to:</strong>{' '}
                {creation.assignedTo
                  .map((uid) => availableUsers.find((u) => u.uid === uid)?.name || uid)
                  .join(', ')}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WorkloadNamingStep;
